import { apiRoutes, getApiUrl } from '@/constants/api';
import { NotAuthenticatedError } from '@/lib/api/errors/not-authenticated-error';
import { handleResponse } from '@/lib/api/helpers/handle-response';

type ClientApiFetcherOptions = RequestInit & {
  skipRefresh?: boolean;
};

let refreshPromise: Promise<boolean> | null = null;

const performRefresh = async (): Promise<boolean> => {
  try {
    const refreshRes = await fetch(getApiUrl(apiRoutes.auth.refreshToken), {
      method: 'POST',
      credentials: 'include',
    });

    if (refreshRes.status === 401) {
      throw new NotAuthenticatedError();
    }

    return refreshRes.ok;
  } catch (error) {
    if (error instanceof NotAuthenticatedError) {
      throw error;
    }
    console.error('Refresh token error:', error);
    return false;
  }
};

export const clientApiFetcher = async <T>(
  input: RequestInfo,
  options: ClientApiFetcherOptions = {},
): Promise<T> => {
  const { skipRefresh = false, ...init } = options;

  init.headers = {
    'Content-Type': 'application/json',
    ...init.headers,
  };
  init.credentials = 'include';

  // First attempt
  let res = await fetch(input, init);

  // If not unauthorized or skipRefresh is true, handle normally
  if (skipRefresh || res.status !== 401) {
    return handleResponse<T>(res);
  }

  // Handle 401 with refresh logic
  try {
    // If already refreshing, wait for the existing refresh
    if (refreshPromise) {
      await refreshPromise;
    } else {
      // Start new refresh
      refreshPromise = performRefresh();
      await refreshPromise;
    }
  } catch (error) {
    // Clear promise on error
    refreshPromise = null;
    throw error;
  } finally {
    // Always clear promise when done
    refreshPromise = null;
  }

  // Retry original request after refresh
  res = await fetch(input, init);

  if (res.status === 401) {
    throw new NotAuthenticatedError();
  }

  return handleResponse<T>(res);
};
